import React, { useEffect, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { SxProps } from "@mui/system";

const HorizontalScrollWrapper = ({
  title,
  customTitleStyles,
  cardRefs,
  containerRef,
  children,
  customMarginRight,
}: {
  title: string;
  customTitleStyles?: React.CSSProperties;
  cardRefs: any;
  containerRef: any;
  children: React.ReactNode;
  customMarginRight?: number;
}) => {
  const theme = useTheme();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Update the scroll buttons visibility based on the scroll position
  useEffect(() => {
    updateScrollButtons();
    const container = containerRef.current;
    if (container === null) return;
    container.addEventListener("scroll", updateScrollButtons);
    return () => container.removeEventListener("scroll", updateScrollButtons);
  }, []);

  const updateScrollButtons = () => {
    const container = containerRef.current;
    if (container === null) return;
    // Check if scroll position allows scrolling to the left
    setCanScrollLeft(container.scrollLeft > 0);
    // Check if scroll position allows scrolling to the right
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 24
    );
  };

  // Scroll by one card width in the specified direction
  const scroll = (direction: string) => {
    const container = containerRef.current;
    const scrollAmount =
      direction === "left" ? -getNextCardWidth(-1) : getNextCardWidth(1);
    if (container) {
      container.scrollBy({
        left:
          scrollAmount +
          (direction === "left"
            ? -(customMarginRight ?? 24)
            : customMarginRight ?? 24),
        behavior: "smooth",
      });
    }
  };

  // Get the width of the next card in the scroll direction
  const getNextCardWidth = (direction: number) => {
    const container = containerRef.current;
    if (container === null) return 0;
    const { scrollLeft, clientWidth } = container;
    const marginRight = customMarginRight ?? 24;
    let accumulatedWidth = 0;

    // Loop over the card refs to get the width of the next card
    for (const card of cardRefs.current) {
      const cardWidth = card?.offsetWidth || 0;
      // Include margin in accumulated width for accurate positioning
      accumulatedWidth += cardWidth + marginRight;

      if (
        (direction > 0 && accumulatedWidth > scrollLeft + clientWidth) ||
        (direction < 0 && accumulatedWidth > scrollLeft)
      ) {
        return cardWidth;
      }
    }
    return 0;
  };

  return (
    <div style={styles.serviceContainer()}>
      <div style={styles.titleContainer()}>
        <div style={{ ...styles.title(theme), ...customTitleStyles }}>
          {title}
        </div>
        {(canScrollLeft || canScrollRight) && (
          <div style={styles.arrowContainer()}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              onClick={() => scroll("left")}
              style={
                canScrollLeft ? { cursor: "pointer" } : { cursor: "default" }
              }
            >
              <path
                d="M13.7497 17.8753C13.3851 17.8752 13.0354 17.7303 12.7776 17.4724L7.27758 11.9724C7.01981 11.7146 6.875 11.3649 6.875 11.0003C6.875 10.6357 7.01981 10.286 7.27758 10.0282L12.7776 4.52817C12.9044 4.39684 13.0561 4.29209 13.2239 4.22003C13.3917 4.14797 13.5721 4.11004 13.7547 4.10845C13.9372 4.10686 14.1183 4.14165 14.2873 4.21079C14.4563 4.27993 14.6098 4.38202 14.7389 4.51113C14.868 4.64023 14.9701 4.79375 15.0392 4.96273C15.1083 5.13172 15.1431 5.31278 15.1416 5.49535C15.14 5.67792 15.102 5.85835 15.03 6.0261C14.9579 6.19386 14.8532 6.34558 14.7218 6.47242L10.194 11.0003L14.7218 15.5282C14.9141 15.7205 15.045 15.9654 15.098 16.2321C15.151 16.4988 15.1238 16.7752 15.0198 17.0264C14.9157 17.2777 14.7395 17.4924 14.5135 17.6435C14.2874 17.7946 14.0216 17.8752 13.7497 17.8753Z"
                fill={
                  !canScrollLeft
                    ? theme.colors_ds.NEUTRAL_BORDER_WEAK
                    : theme.colors_ds.NEUTRAL_ICON_DEFAULT
                }
              />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              onClick={() => scroll("right")}
              style={
                canScrollRight ? { cursor: "pointer" } : { cursor: "default" }
              }
            >
              <path
                d="M8.24971 17.8747C7.9778 17.8746 7.71202 17.794 7.48595 17.6429C7.25989 17.4918 7.0837 17.2771 6.97965 17.0259C6.8756 16.7746 6.84837 16.4982 6.9014 16.2315C6.95444 15.9649 7.08534 15.7199 7.27758 15.5276L11.8055 10.9997L7.27758 6.47183C7.02712 6.2125 6.88852 5.86517 6.89166 5.50465C6.89479 5.14413 7.0394 4.79927 7.29433 4.54433C7.54927 4.28939 7.89413 4.14479 8.25466 4.14165C8.61518 4.13852 8.9625 4.27711 9.22183 4.52758L14.7218 10.0276C14.9796 10.2854 15.1244 10.6351 15.1244 10.9997C15.1244 11.3643 14.9796 11.714 14.7218 11.9718L9.22183 17.4718C8.96403 17.7297 8.61435 17.8746 8.24971 17.8747Z"
                fill={
                  !canScrollRight
                    ? theme.colors_ds.NEUTRAL_BORDER_WEAK
                    : theme.colors_ds.NEUTRAL_ICON_DEFAULT
                }
              />
            </svg>
          </div>
        )}
      </div>

      <div style={styles.cardContainer()} ref={containerRef}>
        {children}
      </div>
    </div>
  );
};

export default HorizontalScrollWrapper;

const styles = {
  innerContainer: () => ({
    width: "100%",
  }),
  serviceContainer: () => ({}),
  titleContainer: () => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }),
  cardContainer: () => ({
    display: "flex",
    overflowX: "auto" as "auto",
    scrollBehavior: "smooth" as "smooth",
    /* Hide scrollbar for Chrome, Safari, and Edge */
    "&::WebkitScrollbar": {
      display: "none" as "none",
    },
    /* Hide scrollbar for Firefox */
    scrollbarWidth: "none" as "none",
  }),
  title: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
  }),
  arrowContainer: () => ({
    display: "flex",
    gap: "8px",
    alignItems: "center",
  }),
};
